import { css } from '@emotion/react';
import { Outlet, ParsedLocation, createFileRoute, redirect, useRouterState } from '@tanstack/react-router';
import dayjs from 'dayjs';
import jwtDecode from 'jwt-decode';
import { QueryClient } from 'react-query';
import { useListIntegrationsSpecsQuery } from '@frontend/api-integration-dashboard';
import { Authn, authnClient } from '@frontend/auth';
import { isTimeExpired, localStorageHelper, PortalUser } from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';
import { useAlert } from '@frontend/design-system';
import { Loader } from '../../components/Loader';
import { Navbar } from '../../components/Navbar';
import {
  clearIntegrationsDashboardStore,
  syncIntegrationsDashboardStoreUser,
  useIntegrationsDashboardShallowStore,
  useIntegrationsDashboardStore,
} from '../../store';
import { checkIsUserAuthenticated } from '../../utils/helpers';

const AuthLayout = () => {
  const alert = useAlert();
  const { isUserRefreshing, setIsUserRefreshing } = useIntegrationsDashboardShallowStore(
    'isUserRefreshing',
    'setIsUserRefreshing'
  );
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { refetch: refetchIntegrationSpecs, dataUpdatedAt } = useListIntegrationsSpecsQuery({
    request: {},
    options: { enabled: !pathname.startsWith('/migrationdashboard') },
  });

  const handleLogoutClick = async () => {
    // Must run before signOut while the user id is still known, since signOut navigates away.
    clearIntegrationsDashboardStore();
    try {
      await authnClient.signOut({ logoutRedirect: `${window.location.origin}/sign-in` });
    } catch (err) {
      console.error('Error during logout:', err);
      alert.error({ message: 'There was an error during logout. Please try again.', autoDismissAfter: 2000 });
    } finally {
      localStorageHelper.delete(['okta-token-storage', 'okta-cache-storage']);
    }
  };

  const handleRefreshClick = async () => {
    try {
      setIsUserRefreshing(true);
      await refetchIntegrationSpecs();
    } catch (e) {
      alert.error({ message: 'Error while refreshing data', autoDismissAfter: 2000 });
    } finally {
      setIsUserRefreshing(false);
    }
  };

  return (
    <>
      <header css={headerStyle}>
        <Navbar
          isUserAuthenticated={checkIsUserAuthenticated()}
          refreshText={`Last Refresh at ${dayjs(dataUpdatedAt).format('MMM D, h:mm:ss A')}`}
          isUserRefreshing={isUserRefreshing}
          onLogoutClick={handleLogoutClick}
          onRefreshClick={handleRefreshClick}
        />
      </header>
      <main>
        <Outlet />
      </main>
    </>
  );
};

const checkAuth = async ({
  context,
  location,
}: {
  context: { auth: Authn; queryClient: QueryClient; user: PortalUser | undefined };
  location: ParsedLocation;
}) => {
  const tokens = await context.auth.getTokens();

  // Check if user is still authorized
  if (tokens.weaveToken) {
    const decoded = jwtDecode<{ expBuffer: number; exp: number }>(tokens.weaveToken);

    // User is already authenticated and token is valid
    if (context.user && !isTimeExpired(decoded.expBuffer) && !isTimeExpired(decoded.exp)) {
      http.setLocationIdHeader('weave');
      useIntegrationsDashboardStore.getState().setIsUserAuthenticated(true);
      await syncIntegrationsDashboardStoreUser();
      return { isAuthenticated: true, user: context.user };
    } else if (!isTimeExpired(decoded.expBuffer) && isTimeExpired(decoded.exp)) {
      // Refresh the weave token if still within the refresh window

      try {
        await context.auth.refreshWeaveToken();
        http.setLocationIdHeader('weave');
        useIntegrationsDashboardStore.getState().setIsUserAuthenticated(true);
      } catch (e) {
        console.error('Error refreshing weave token:', e);
        return redirect({
          to: '/sign-in',
        });
      }
    } else if (isTimeExpired(decoded.expBuffer) && isTimeExpired(decoded.exp)) {
      // Redirect to login if token is expired and outside of refresh windows
      sessionStorage.setItem('redirect', location.href === '/' ? '/dashboard' : location.href);
      useIntegrationsDashboardStore.getState().setIsUserAuthenticated(false);
      localStorageHelper.delete([
        'token',
        'user',
        'jti',
        'okta-token-storage',
        'token_received_at',
        'sign_in_fingerprint',
        'okta-cache-storage',
        'login_features_checked',
        'login_features_json',
      ]);
      // Detach from the expired user so the sign-in reset can't overwrite their saved preferences.
      await syncIntegrationsDashboardStoreUser();
      return redirect({
        to: '/sign-in',
      });
    }
  } else {
    // Redirect to login if no token is found
    useIntegrationsDashboardStore.getState().setIsUserAuthenticated(false);
    sessionStorage.setItem('redirect', location.href === '/' ? '/dashboard' : location.href);
    return redirect({
      to: '/sign-in',
    });
  }

  let user;

  try {
    user = await context.queryClient.fetchQuery({
      queryKey: 'user',
      queryFn: () => {
        return context.auth.fetchUser();
      },
      staleTime: 1000 * 60 * 60 * 24, // 1 day
    });
  } catch (e) {
    console.error('Error fetching user:', e);
  }

  if (user && !http.headers.has('Location-ID')) {
    http.setLocationIdHeader('weave');
  }

  await syncIntegrationsDashboardStoreUser();

  return { isAuthenticated: true, user };
};

export const Route = createFileRoute('/_auth')({
  // Before loading, authenticate the user via our auth context
  // This will also happen during prefetching
  pendingComponent: () => <Loader />,
  beforeLoad: checkAuth,
  component: AuthLayout,
});

const headerStyle = css`
  position: sticky;
  top: 0;
  z-index: 100;
`;
